import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { ADMIN_BTN_GHOST, ADMIN_BTN_PRIMARY, ADMIN_CARD, ADMIN_SECTION_TITLE } from './adminTheme';

/**
 * Admin HTOP tab — capture process snapshots, retain ~24h for debug.
 */
export default function AdminHtopPanel() {
  const base = getApiBaseUrl();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`${base}/admin/htop?limit=24`, adminFetchInit());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (err) {
      setError(String(err?.message || err));
    }
  }, [base]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) return;
      setError('');
      try {
        const res = await fetch(`${base}/admin/htop/${encodeURIComponent(id)}`, adminFetchInit());
        if (!res.ok) throw new Error(res.status === 404 ? 'Snapshot expired' : `HTTP ${res.status}`);
        const json = await res.json();
        setSelected(json.snapshot || null);
        setOutput(String(json.snapshot?.output || ''));
      } catch (err) {
        setError(String(err?.message || err));
        setSelected(null);
        setOutput('');
      }
    },
    [base],
  );

  const capture = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${base}/admin/htop`, { ...adminFetchInit(), method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const snap = json.snapshot;
      setSelected(snap || null);
      setOutput(String(snap?.output || ''));
      await loadList();
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setBusy(false);
    }
  }, [base, loadList]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  return (
    <div className="space-y-4">
      <section className={ADMIN_CARD}>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className={ADMIN_SECTION_TITLE}>HTOP / process snapshot</h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-400">
              Captures host/container process view (htop batch, else top/ps). Snapshots expire after 24 hours
              (Redis + /tmp file) for debug.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ADMIN_BTN_GHOST} onClick={loadList} disabled={busy}>
              Refresh list
            </button>
            <button type="button" className={ADMIN_BTN_PRIMARY} onClick={capture} disabled={busy}>
              {busy ? 'Capturing…' : 'Capture now'}
            </button>
          </div>
        </div>
        {error ? <p className="mb-3 text-xs font-semibold text-rose-300">{error}</p> : null}

        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="max-h-[520px] overflow-auto rounded-xl border border-white/10 bg-slate-950/50">
            {items.length === 0 ? (
              <p className="p-4 text-xs text-slate-500">No snapshots yet — capture one.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((it) => {
                  const active = selected?.id === it.id;
                  return (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => loadDetail(it.id)}
                        className={`w-full px-3 py-2.5 text-left transition hover:bg-white/5 ${
                          active ? 'bg-cyan-500/10 ring-1 ring-inset ring-cyan-400/30' : ''
                        }`}
                      >
                        <p className="font-mono text-[11px] text-slate-200">{it.ts}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                          {it.tool || 'unknown'} · {it.bytes != null ? `${it.bytes} B` : '—'}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <pre className="max-h-[520px] overflow-auto rounded-xl border border-white/10 bg-slate-950/70 p-3 font-mono text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap">
            {output || 'Select or capture a snapshot to view output.'}
          </pre>
        </div>
        {selected?.file_path ? (
          <p className="mt-3 font-mono text-[10px] text-slate-500">Saved: {selected.file_path}</p>
        ) : null}
      </section>
    </div>
  );
}
